import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TreeNode } from './TreeNode';
import { TreeItem } from './types';
import { buildTree, filterTreeWithDescendants } from './utils';

// NOTE(happyhyep): Search Context
interface TreeSearchContextType {
  search: string;
  setSearch: (value: string) => void;
}

const TreeSearchContext = createContext<TreeSearchContextType | null>(null);

export const useTreeSearch = () => {
  const ctx = useContext(TreeSearchContext);
  if (!ctx) throw new Error('useTreeSearch must be used within <TreeWithSearch>');
  return ctx;
};

// NOTE(happyhyep): Search Input Component
export interface TreeSearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  placeholder?: string;
}

const TreeSearchInput: React.FC<TreeSearchInputProps> = ({
  placeholder = '검색...',
  className = '',
  ...rest
}) => {
  const { search, setSearch } = useTreeSearch();

  return (
    <input
      type="text"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 ${className}`}
      style={{ fontSize: '14px' }}
      {...rest}
    />
  );
};

// NOTE(happyhyep): Main TreeWithSearch Component
export interface TreeWithSearchProps<T> {
  items: TreeItem<T>[];
  defaultExpandAll?: boolean;
  renderLabel: (data: T, highlight?: string) => React.ReactNode;
  renderLeaf?: (data: T) => React.ReactNode;
  searchFn: (data: T, keyword: string) => boolean;
  children?: React.ReactNode;
  onItemClick?: (item: TreeItem<T>) => void;
  selectedId?: string;
  className?: string;
}

const TreeWithSearchComponent = <T,>({
  items,
  renderLabel,
  renderLeaf,
  searchFn,
  defaultExpandAll = false,
  children,
  onItemClick,
  selectedId,
  className = '',
}: TreeWithSearchProps<T>) => {
  const [search, setSearch] = useState('');

  const [manualOpenFolders, setManualOpenFolders] = useState<Set<string>>(
    () => new Set(defaultExpandAll ? items.map((el) => el.id) : []),
  );
  const [searchOpenFolders, setSearchOpenFolders] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (defaultExpandAll) {
      setManualOpenFolders(new Set(items.map((el) => el.id)));
    }
  }, [items, defaultExpandAll]);

  const isSearching = search.trim().length > 0;
  const activeOpenFolders = isSearching ? searchOpenFolders : manualOpenFolders;

  const treeData = useMemo(() => buildTree(items), [items]);

  const filteredData = useMemo(() => {
    if (!isSearching || !searchFn) return treeData;
    return filterTreeWithDescendants(treeData, search, (item) => searchFn(item.data, search));
  }, [search, treeData, searchFn, isSearching]);

  useEffect(() => {
    if (!isSearching || !searchFn) return;

    const matched = filterTreeWithDescendants(treeData, search, (item) =>
      searchFn(item.data, search),
    );

    const idsToOpen = new Set<string>();
    const collectAllIds = (items: TreeItem<T>[]) => {
      for (const item of items) {
        idsToOpen.add(item.id);
        if (item.children) collectAllIds(item.children);
      }
    };
    collectAllIds(matched);

    setSearchOpenFolders(idsToOpen);
  }, [search, searchFn, treeData, isSearching]);

  const toggle = (id: string) => {
    const setState = isSearching ? setSearchOpenFolders : setManualOpenFolders;
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <TreeSearchContext.Provider value={{ search, setSearch }}>
      <div className={className}>
        {children}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {filteredData.map((item) => (
            <TreeNode
              key={item.id}
              item={item}
              level={0}
              isOpen={activeOpenFolders.has(item.id)}
              toggle={toggle}
              renderLabel={(data) => renderLabel(data, isSearching ? search : undefined)}
              renderLeaf={renderLeaf}
              openFolders={activeOpenFolders}
              onItemClick={onItemClick}
              selectedId={selectedId}
            />
          ))}
        </ul>
      </div>
    </TreeSearchContext.Provider>
  );
};

// 런타임에 Input 속성 추가
TreeWithSearchComponent.Input = TreeSearchInput;

// 타입 단언으로 Input 속성을 타입에 포함
export const TreeWithSearch = TreeWithSearchComponent as typeof TreeWithSearchComponent & {
  Input: typeof TreeSearchInput;
};
